import { useState, useMemo, useCallback } from 'react';
import { createChatbotService, ChatMessage } from '../services/chatbot';

const geminiApiKey = import.meta.env.VITE_GEMINI_API_KEY;

if (!geminiApiKey) {
    console.error("VITE_GEMINI_API_KEY is not set in your environment variables. The chatbot will not work.");
}

export const useChatbot = () => {
    const chatbotService = useMemo(() => {
        return geminiApiKey ? createChatbotService({ geminiApiKey }) : null;
    }, []);

    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [navigationPath, setNavigationPath] = useState<string | null>(null);

    const sendMessage = useCallback(async (newMessage: string) => {
        if (!chatbotService) {
            setError("Chatbot is not configured. Please check your API key.");
            return;
        }

        const userMessage: ChatMessage = { role: 'user', text: newMessage };
        setMessages(prevMessages => [...prevMessages, userMessage]);
        setIsLoading(true);
        setError(null);
        setNavigationPath(null);

        const currentHistory = [...messages, userMessage];
        const response = await chatbotService.getResponse(currentHistory, newMessage);

        if (response.success) {
            if (response.text) {
                const modelMessage: ChatMessage = { role: 'model', text: response.text };
                setMessages(prevMessages => [...prevMessages, modelMessage]);
            }
            if (response.navigation) {
                setNavigationPath(response.navigation);
            }
        } else {
            const errorMessage = response.error || "An unknown error occurred.";
            setError(errorMessage);
            const errorResponse: ChatMessage = { role: 'model', text: `Sorry, I encountered an error: ${errorMessage}` };
            setMessages(prevMessages => [...prevMessages, errorResponse]);
        }

        setIsLoading(false);
    }, [chatbotService, messages]);

    const clearNavigation = useCallback(() => {
        setNavigationPath(null);
    }, []);

    return {
        messages,
        isLoading,
        error,
        sendMessage,
        navigationPath,
        clearNavigation,
    };
};